<template>
  <div>
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Balance Sheet Report</h5>
        <p class="page-sub">Snapshot of financial position as of a specific date</p>
      </div>
      <div class="page-actions">

        <!-- As Of Date — exact original single-date filter -->
        <div class="date-field">
          <font-awesome-icon icon="calendar" class="date-icon" />
          <Datepicker
            v-model="tableData.asOfDate"
            :enableTimePicker="false"
            :hideInputIcon="true"
            class="date-picker"
            placeholder="As of date"
          />
        </div>

        <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
          <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <font-awesome-icon v-else icon="search" class="me-1" />
          {{ crudLoading ? 'Loading...' : 'Search' }}
        </button>

        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="exportPDF('balance_sheet_report')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="report-wrap">
        <div class="skeleton skeleton--letterhead mb-4"></div>
        <div class="report-cols">
          <div class="report-col">
            <div class="skeleton skeleton--sechdr mb-2"></div>
            <div class="skeleton skeleton--row mb-2" v-for="n in 5" :key="'a'+n"></div>
            <div class="skeleton skeleton--total mt-2"></div>
          </div>
          <div class="report-col">
            <div class="skeleton skeleton--sechdr mb-2"></div>
            <div class="skeleton skeleton--row mb-2" v-for="n in 3" :key="'l'+n"></div>
            <div class="skeleton skeleton--total mt-2 mb-4"></div>
            <div class="skeleton skeleton--sechdr mb-2"></div>
            <div class="skeleton skeleton--row mb-2" v-for="n in 2" :key="'e'+n"></div>
            <div class="skeleton skeleton--total mt-2"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!hasData" class="data-card">
      <div class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="balance-scale" />
        </div>
        <p class="state-title">No report data</p>
        <p class="state-sub">Select a date and click Search to generate the balance sheet.</p>
      </div>
    </div>

    <!-- Report -->
    <div v-else class="report-wrap" ref="pdfContent">

      <!-- Letterhead -->
      <div class="report-letterhead">
        <div class="letterhead-left">
          <div class="letterhead-icon">
            <font-awesome-icon icon="balance-scale" />
          </div>
          <div>
            <h2 class="letterhead-title">Balance Sheet</h2>
            <p class="letterhead-sub">As of {{ formatDate(tableData.asOfDate) }}</p>
          </div>
        </div>
        <div class="letterhead-right">
          <div class="balance-pill" :class="isBalanced ? 'balance-pill--ok' : 'balance-pill--err'">
            <font-awesome-icon :icon="isBalanced ? 'check-circle' : 'exclamation-circle'" class="me-1" />
            {{ isBalanced ? 'Balanced' : 'Unbalanced' }}
          </div>
          <div class="totals-bar">
            <div class="totals-bar-item">
              <span class="totals-bar-label">Total Assets</span>
              <span class="totals-bar-value totals-bar-value--blue">{{ formatPrice(totalAssets) }}</span>
            </div>
            <span class="totals-bar-sep">vs</span>
            <div class="totals-bar-item">
              <span class="totals-bar-label">Liabilities + Equity</span>
              <span class="totals-bar-value">{{ formatPrice(totalLiabilities + totalEquity) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-column grid -->
      <div class="report-cols">

        <!-- Assets -->
        <div class="report-col">
          <div class="report-section">
            <div class="sec-hdr sec-hdr--blue">
              <font-awesome-icon icon="university" />
              <span>Assets</span>
              <span class="sec-hdr-total">{{ formatPrice(totalAssets) }}</span>
            </div>
            <table class="rep-table">
              <thead><tr><th>Account</th><th class="tr">Balance</th></tr></thead>
              <tbody>
                <tr v-for="asset in assets" :key="asset.code">
                  <td><div class="acc-cell"><span class="acc-code">{{ asset.code }}</span><span class="acc-name">{{ asset.name }}</span></div></td>
                  <td class="tr fw6">{{ formatPrice(asset.balance) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td class="tlabel">Total Assets</td><td class="tr tamount tamount--blue">{{ formatPrice(totalAssets) }}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Liabilities + Equity -->
        <div class="report-col">

          <div class="report-section mb4">
            <div class="sec-hdr sec-hdr--red">
              <font-awesome-icon icon="hand-holding-usd" />
              <span>Liabilities</span>
              <span class="sec-hdr-total">{{ formatPrice(totalLiabilities) }}</span>
            </div>
            <table class="rep-table">
              <thead><tr><th>Account</th><th class="tr">Balance</th></tr></thead>
              <tbody>
                <tr v-for="liability in liabilities" :key="liability.code">
                  <td><div class="acc-cell"><span class="acc-code">{{ liability.code }}</span><span class="acc-name">{{ liability.name }}</span></div></td>
                  <td class="tr fw6">{{ formatPrice(liability.balance) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td class="tlabel">Total Liabilities</td><td class="tr tamount tamount--red">{{ formatPrice(totalLiabilities) }}</td></tr>
              </tfoot>
            </table>
          </div>

          <div class="report-section">
            <div class="sec-hdr sec-hdr--purple">
              <font-awesome-icon icon="chart-pie" />
              <span>Equity</span>
              <span class="sec-hdr-total">{{ formatPrice(totalEquity) }}</span>
            </div>
            <table class="rep-table">
              <thead><tr><th>Account</th><th class="tr">Balance</th></tr></thead>
              <tbody>
                <tr v-for="eq in equity" :key="eq.code">
                  <td><div class="acc-cell"><span class="acc-code">{{ eq.code }}</span><span class="acc-name">{{ eq.name }}</span></div></td>
                  <td class="tr fw6">{{ formatPrice(eq.balance) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td class="tlabel">Total Equity</td><td class="tr tamount tamount--purple">{{ formatPrice(totalEquity) }}</td></tr>
              </tfoot>
            </table>
          </div>

          <div class="grand-total-row">
            <span class="grand-total-label">Total Liabilities + Equity</span>
            <span class="grand-total-value">{{ formatPrice(totalLiabilities + totalEquity) }}</span>
          </div>

        </div>
      </div>

      <div class="rep-footer">Generated by Mapiibs Solution</div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import { useStore } from 'vuex';
import ReportHeaderTab from './components/reportheadertab.vue';
import moment from 'moment';

export default {
  components: { ReportHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      assets: [], liabilities: [], equity: [],
      totalAssets: 0, totalLiabilities: 0, totalEquity: 0,
      loading: false, crudLoading: false, query: null,

      // Exact original tableData — nothing changed
      tableData: { page: 1,
        asOfDate: new Date(),
        draw: 0, length: 10, search: '', column: 0, dir: 'desc', id: '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    hasData()    { return this.assets.length > 0 || this.liabilities.length > 0 || this.equity.length > 0; },
    isBalanced() { return Math.abs(this.totalAssets - (this.totalLiabilities + this.totalEquity)) < 0.01; },
  },

  created() { this.fetchData(); },

  methods: {
    // Exact original fetchData — no params changed
    fetchData(url = '/accounting/report/balance-sheet') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;

      if (this.crudLoading && this.query) {
        this.crudLoading = false;
      }

      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(response => {
          this.loading = false; this.crudLoading = false;
          if (!response.data.status == 200) return;
          // API returns objects keyed by index — convert to arrays
          const d               = response.data.data;
          this.assets           = Object.values(d.assets      ?? {});
          this.liabilities      = Object.values(d.liabilities ?? {});
          this.equity           = Array.isArray(d.equity) ? d.equity : Object.values(d.equity ?? {});
          this.totalAssets      = d.totalAssets      ?? 0;
          this.totalLiabilities = d.totalLiabilities ?? 0;
          this.totalEquity      = d.totalEquity      ?? 0;
        })
        .catch(error => {
          this.loading = false; this.crudLoading = false;
          if ([400, 401, 403, 500].includes(error.response?.status)) {
            this.toast.error(error.response.data.message);
          }
        });
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },

    formatPrice(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

.date-field { display:flex; align-items:center; gap:8px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:5px 12px; }
.date-icon  { color:#7a90b0; font-size:13px; }
:deep(.date-picker)       { border:none !important; background:transparent !important; box-shadow:none !important; }
:deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; padding:3px 0 !important; width:130px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; }

.skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--letterhead { height:90px; }
.skeleton--sechdr     { height:42px; border-radius:10px; }
.skeleton--row        { height:17px; }
.skeleton--total      { height:22px; width:50%; margin-left:auto; }
.mb-2 { margin-bottom:8px; } .mb-4 { margin-bottom:16px; } .mt-2 { margin-top:8px; }

.data-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

.report-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; }

.report-letterhead { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:24px; border-bottom:2px solid #e4eaf2; margin-bottom:28px; flex-wrap:wrap; }
.letterhead-left   { display:flex; align-items:center; gap:16px; }
.letterhead-icon   { width:52px; height:52px; border-radius:14px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
.letterhead-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
.letterhead-sub    { font-size:13px; color:#7a90b0; margin:0; }
.letterhead-right  { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }

.balance-pill      { display:inline-flex; align-items:center; border-radius:8px; padding:6px 14px; font-size:12.5px; font-weight:700; border:1px solid transparent; }
.balance-pill--ok  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.balance-pill--err { background:#fff0f0; color:#e03535; border-color:#fac5c5; }

.totals-bar       { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 16px; }
.totals-bar-item  { display:flex; flex-direction:column; gap:2px; }
.totals-bar-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.totals-bar-value { font-size:16px; font-weight:800; color:#1a2b45; }
.totals-bar-value--blue { color:#004AAD; }
.totals-bar-sep   { font-size:12px; font-weight:700; color:#aab8cc; }

.report-cols { display:grid; grid-template-columns:1fr 1fr; gap:24px; align-items:start; }
.report-col  { display:flex; flex-direction:column; }

.report-section     { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.report-section.mb4 { margin-bottom:16px; }

.sec-hdr        { display:flex; align-items:center; gap:10px; padding:12px 16px; font-size:14px; font-weight:700; }
.sec-hdr--blue   { background:rgba(0,74,173,.07); color:#004AAD; }
.sec-hdr--red    { background:rgba(224,53,53,.07); color:#e03535; }
.sec-hdr--purple { background:rgba(124,58,237,.07); color:#7c3aed; }
.sec-hdr-total   { margin-left:auto; font-size:13px; font-weight:600; opacity:.8; }

.rep-table                 { width:100%; border-collapse:collapse; }
.rep-table thead th        { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:9px 14px; border-bottom:1px solid #e4eaf2; background:#f8fafc; }
.rep-table tbody tr        { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.rep-table tbody tr:last-child { border-bottom:none; }
.rep-table tbody tr:hover  { background:#f8fafc; }
.rep-table tbody td        { padding:10px 14px; font-size:13.5px; }
.rep-table tfoot tr        { background:#f8fafc; }
.rep-table tfoot td        { padding:11px 14px; border-top:2px solid #e4eaf2; font-size:13.5px; }
.tr  { text-align:right; }
.fw6 { font-weight:600; color:#1a2b45; }

.acc-cell { display:flex; align-items:center; gap:8px; }
.acc-code { font-family:monospace; font-size:11px; font-weight:600; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:5px; padding:1px 6px; flex-shrink:0; }
.acc-name { font-size:13.5px; color:#1a2b45; }

.tlabel          { font-size:13px; font-weight:700; color:#1a2b45; }
.tamount         { font-size:15px; font-weight:800; }
.tamount--blue   { color:#004AAD; }
.tamount--red    { color:#e03535; }
.tamount--purple { color:#7c3aed; }

.grand-total-row   { display:flex; justify-content:space-between; align-items:center; margin-top:16px; padding:13px 16px; background:rgba(0,74,173,.06); border:1px solid #c5d6f5; border-radius:10px; }
.grand-total-label { font-size:13px; font-weight:700; color:#004AAD; }
.grand-total-value { font-size:20px; font-weight:800; color:#004AAD; }

.rep-footer { text-align:center; margin-top:28px; padding-top:20px; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }

.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
.dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
.dropdown-item:hover { background:#f4f7fb; }

@media print {
  .page-header,.btn-primary-action,.btn-outline,.dropdown { display:none !important; }
  .report-wrap { border:none; padding:0; }
}
@media(max-width:991px) { .report-cols { grid-template-columns:1fr; } }
@media(max-width:767px) {
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .report-letterhead { flex-direction:column; }
  .letterhead-right { align-items:flex-start; }
  .report-wrap { padding:16px; }
}
</style>